import { getDefaultOutputKey } from '../Dashboard/controlRegistry';

/** All binding rows for editing (does not drop incomplete rows). */
export function coercePanelBindingRows(source) {
  const bindings = Array.isArray(source?.bindings) ? source.bindings : [];
  return bindings.map((b) => ({
    targetKey: String(b?.targetKey || '').trim(),
    sourcePath: String(b?.sourcePath || '').trim(),
    required: b?.required !== false,
    defaultValue: b?.defaultValue,
  }));
}

export function normalizePanelBindings(source) {
  return coercePanelBindingRows(source).filter((b) => b.targetKey && b.sourcePath);
}

/**
 * Walk a dotted path on flow output. Tries `result.*` as-is, then without a leading `result`
 * segment when the root is already the unwrapped payload (common after extractStructuredOutput).
 * @param {unknown} root
 * @param {string} sourcePath e.g. result.screening.queue.rows
 * @returns {unknown}
 */
export function resolveStructuredPath(root, sourcePath) {
  const raw = String(sourcePath || '').trim();
  if (!raw || root == null || typeof root !== 'object') return undefined;
  const parts = raw.split('.').filter(Boolean);
  if (!parts.length) return undefined;
  const walk = (segments) => {
    let cur = root;
    for (const seg of segments) {
      if (cur == null || typeof cur !== 'object') return undefined;
      cur = cur[seg];
    }
    return cur;
  };
  let v = walk(parts);
  if (v !== undefined) return v;
  if (parts[0] === 'result') {
    v = walk(parts.slice(1));
    if (v !== undefined) return v;
  }
  return undefined;
}

/**
 * Build the object passed to dashboard components (rows/columns, content, items, …) from
 * non-app binding rows and the stored per-panel flow output object.
 * @param {{ source?: object } | null | undefined} panel
 * @param {unknown} flowRoot
 * @returns {Record<string, unknown> | undefined}
 */
export function assembleBoundPanelPayload(panel, flowRoot) {
  const bindings = normalizePanelBindings(panel?.source);
  const rows = bindings.filter((b) => {
    const sp = String(b.sourcePath || '').trim();
    return sp && !sp.startsWith('app.');
  });
  if (!rows.length || flowRoot == null || typeof flowRoot !== 'object') return undefined;
  const out = {};
  rows.forEach((b) => {
    const v = resolveStructuredPath(flowRoot, b.sourcePath);
    if (v !== undefined) out[b.targetKey] = v;
  });
  return Object.keys(out).length ? out : undefined;
}

/** Rich text panels expect `{ content }`; flows may emit a plain string at result.summary. */
export function normalizeRichTextBindingValue(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && value.content != null) {
    return typeof value.content === 'string' ? value.content : String(value.content);
  }
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
}

export function validatePanelBindingContract(panel, outputHints = []) {
  const errors = [];
  const source = panel?.source || {};
  const flowRef = String(source.flowRef || '').trim();
  const outputPath = String(source.outputPath || '').trim();
  const rows = coercePanelBindingRows(source);
  const completeBindings = normalizePanelBindings(source);

  if (panel?.type === 'component' && !flowRef) {
    errors.push('Missing source.flowRef');
  }
  if (panel?.type === 'component' && !outputPath && completeBindings.length === 0) {
    errors.push('Set source.outputPath or at least one binding mapping.');
  }
  if (outputPath && outputHints.length > 0 && !outputHints.includes(outputPath)) {
    errors.push(`outputPath "${outputPath}" not in linked flow output hints.`);
  }

  const seen = new Set();
  rows.forEach((b, i) => {
    const hasAny = Boolean(b.targetKey || b.sourcePath);
    if (!hasAny) return;
    if (b.targetKey && !b.sourcePath) errors.push(`bindings[${i}] missing sourcePath`);
    if (b.sourcePath && !b.targetKey) errors.push(`bindings[${i}] missing targetKey`);
    if (b.targetKey) {
      if (seen.has(b.targetKey)) errors.push(`Duplicate binding targetKey "${b.targetKey}"`);
      seen.add(b.targetKey);
    }
  });

  return { valid: errors.length === 0, errors, bindings: completeBindings };
}

const PANEL_CONTRACTS = {
  table: {
    label: 'Table',
    required: ['rows'],
    optional: ['columns'],
    examples: ['result.table.rows', 'result.queue.rows'],
  },
  rich_text: {
    label: 'Detail / rich text',
    required: ['content'],
    optional: ['title', 'subtitle'],
    examples: ['result.detail.content', 'result.summary'],
  },
  stats: {
    label: 'Metrics',
    required: ['items'],
    optional: ['summary'],
    examples: ['result.metrics.items', 'result.kpis'],
  },
  timeline: {
    label: 'Timeline',
    required: ['events'],
    optional: ['title'],
    examples: ['result.timeline.events', 'result.events'],
  },
  list: {
    label: 'Checklist / list',
    required: ['items'],
    optional: ['title'],
    examples: ['result.checklist.items', 'result.items'],
  },
  grid: {
    label: 'Scorecard / grid',
    required: ['items'],
    optional: ['columns'],
    examples: ['result.scorecard.dimensions', 'result.grid.items'],
  },
  image: {
    label: 'Document / image',
    required: ['url'],
    optional: ['caption', 'alt'],
    examples: ['result.document.url', 'result.image.url'],
  },
};

export function getPanelContractPreset(panel) {
  const ct = panel?.componentType || 'table';
  return PANEL_CONTRACTS[ct] || {
    label: ct,
    required: [getDefaultOutputKey(ct)],
    optional: [],
    examples: [`result.${getDefaultOutputKey(ct)}`],
  };
}

export function getBindingSourcePathOptions(panel, outputHints = []) {
  const contract = getPanelContractPreset(panel);
  const hinted = Array.isArray(outputHints) ? outputHints : [];
  const outputPath = String(panel?.source?.outputPath || '').trim();
  const roots = [...new Set([
    outputPath,
    ...hinted,
    ...contract.examples,
    'result',
    'variables',
    'node_outputs',
    'app.interviewTranscript',
    'app.chatHistory',
  ].filter(Boolean))];
  const targetPaths = [...contract.required, ...contract.optional]
    .flatMap((target) => roots.map((root) => (root.endsWith(`.${target}`) ? root : `${root}.${target}`)));
  return [...new Set([...roots, ...targetPaths])];
}

export function getPanelContractDiagnostics(panel, outputHints = []) {
  const messages = [];
  const contract = getPanelContractPreset(panel);
  const bindings = normalizePanelBindings(panel?.source);
  const targets = new Set(bindings.map((b) => b.targetKey));
  contract.required.forEach((target) => {
    if (!targets.has(target)) {
      messages.push(`Recommended binding target "${target}" is missing for ${contract.label} panels.`);
    }
  });
  bindings.forEach((binding) => {
    if (!binding.sourcePath.startsWith('app.') && outputHints.length && !outputHints.some((hint) => binding.sourcePath === hint || binding.sourcePath.startsWith(`${hint}.`))) {
      messages.push(`Binding "${binding.targetKey}" sourcePath "${binding.sourcePath}" is not covered by linked flow hints.`);
    }
  });
  return { severity: messages.length ? 'warning' : 'success', messages, contract };
}

export function buildPanelBindingPreview(panel) {
  const source = panel?.source || {};
  const out = {};
  normalizePanelBindings(source).forEach((b) => {
    out[b.targetKey] = b.defaultValue != null && b.defaultValue !== ''
      ? b.defaultValue
      : `{{${b.sourcePath}}}`;
  });
  return out;
}

export function buildEnhancedPanelBindingPreview(panel, outputHints = []) {
  const contract = getPanelContractPreset(panel);
  const payload = buildPanelBindingPreview(panel);
  return {
    panelId: panel?.id || '',
    componentType: panel?.componentType || '',
    expectedContract: {
      label: contract.label,
      required: contract.required,
      optional: contract.optional,
    },
    outputPath: panel?.source?.outputPath || '',
    payload,
    diagnostics: getPanelContractDiagnostics(panel, outputHints).messages,
  };
}

export function getPanelBindingPresets(panel, outputHints = []) {
  const sourcePath = String(getSuggestedBindingSourcePaths(panel, outputHints)[0] || 'result').trim();
  const contract = getPanelContractPreset(panel);
  return [
    {
      id: 'contract-default',
      label: `${contract.label} defaults`,
      bindings: [
        ...contract.required.map((targetKey) => ({ targetKey, sourcePath: `${sourcePath}.${targetKey}`, required: true })),
        ...contract.optional.map((targetKey) => ({ targetKey, sourcePath: `${sourcePath}.${targetKey}`, required: false })),
      ],
    },
    {
      id: 'direct-output',
      label: 'Direct output',
      bindings: [{ targetKey: getDefaultOutputKey(panel?.componentType || 'table'), sourcePath, required: true }],
    },
  ];
}

export function getSuggestedBindingSourcePaths(panel, outputHints = []) {
  const defaultKey = getDefaultOutputKey(panel?.componentType || 'table');
  const hinted = Array.isArray(outputHints) ? outputHints : [];
  return [...new Set([
    panel?.source?.outputPath || '',
    defaultKey,
    ...hinted,
    `result.${defaultKey}`,
    `data.${defaultKey}`,
  ].filter(Boolean))];
}

export function getStarterBindingsForPanel(panel, outputHints = []) {
  const preset = getPanelBindingPresets(panel, outputHints)[0];
  if (preset?.bindings?.length) return preset.bindings;
  const primaryPath = getSuggestedBindingSourcePaths(panel, outputHints)[0] || 'result';
  const sourcePath = String(primaryPath || 'result').trim();
  const ct = panel?.componentType || 'table';
  if (ct === 'table') {
    return [
      { targetKey: 'rows', sourcePath: `${sourcePath}.rows`, required: true },
      { targetKey: 'columns', sourcePath: `${sourcePath}.columns`, required: false },
    ];
  }
  if (ct === 'chart') {
    return [
      { targetKey: 'labels', sourcePath: `${sourcePath}.labels`, required: true },
      { targetKey: 'datasets', sourcePath: `${sourcePath}.datasets`, required: true },
    ];
  }
  if (ct === 'stats' || ct === 'list' || ct === 'grid' || ct === 'timeline') {
    const collectionKey = ct === 'timeline' ? 'events' : 'items';
    return [{ targetKey: collectionKey, sourcePath: `${sourcePath}.${collectionKey}`, required: true }];
  }
  if (ct === 'map') {
    return [
      { targetKey: 'markers', sourcePath: `${sourcePath}.markers`, required: true },
      { targetKey: 'center', sourcePath: `${sourcePath}.center`, required: false },
    ];
  }
  if (ct === 'image') {
    return [{ targetKey: 'url', sourcePath: `${sourcePath}.url`, required: true }];
  }
  if (ct === 'rich_text') {
    return [{ targetKey: 'content', sourcePath, required: true }];
  }
  if (ct === 'form') {
    return [{ targetKey: 'form_prefill', sourcePath, required: false }];
  }
  return [{ targetKey: getDefaultOutputKey(ct), sourcePath, required: true }];
}

export function getPanelBindingDiagnostics(panel, outputHints = []) {
  const warnings = [];
  const source = panel?.source || {};
  const outputPath = String(source.outputPath || '').trim();
  const bindings = normalizePanelBindings(source);
  const complete = validatePanelBindingContract(panel, outputHints);

  if (!complete.valid) {
    return { severity: 'error', messages: complete.errors };
  }
  if (panel?.type !== 'component') {
    return { severity: 'info', messages: [] };
  }
  if (!outputPath && bindings.length > 0) {
    warnings.push('Bindings are set, but source.outputPath is empty. Runtime will rely on binding source paths only.');
  }
  if (outputPath && bindings.length === 0) {
    warnings.push('outputPath is set, but no binding rows exist. The panel will rely on outputKey/default component behavior.');
  }
  if (panel?.componentType === 'form' && (!Array.isArray(panel?.config?.formFields) || panel.config.formFields.length === 0)) {
    warnings.push('Form panels need config.formFields to be useful at runtime.');
  }
  if (bindings.length > 0 && complete.bindings.length === 0) {
    warnings.push('Binding rows are incomplete. Fill both targetKey and sourcePath.');
  }
  return { severity: warnings.length ? 'warning' : 'success', messages: warnings };
}

